// Invitations tab (handoff Section 7A.5): who has been invited, delivery
// status, reminders, and the roster tools. Shows delivery status by name but
// never response content. Invitation tokens are never present in any response
// here and are never rendered.

import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  exportUrls,
  healthApi,
  HealthApiError,
  type CycleDetail,
  type InvitationSummary,
  type InviteSuggestion,
} from './api';
import {
  ROLE_LABELS,
  ROLE_ORDER,
  formatNzDateShort,
  invitationStatusLabel,
  parseInviteeLines,
  roleLabel,
} from './format';

function statusPillClass(status: string): string {
  switch (status) {
    case 'submitted':
      return 'pill pill-success';
    case 'opened':
    case 'in_progress':
      return 'pill pill-info';
    case 'invited':
    case 'delivered':
      return 'pill pill-warn';
    case 'bounced':
    case 'declined':
      return 'pill pill-danger';
    default:
      return 'pill pill-neutral';
  }
}

export function InvitationsTab({
  detail,
  reload,
}: {
  detail: CycleDetail;
  reload: () => void;
}): JSX.Element {
  const { cycle } = detail;
  const [invitations, setInvitations] = useState<InvitationSummary[] | null>(null);
  const [loadProblem, setLoadProblem] = useState<'forbidden' | 'error' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const navigate = useNavigate();

  const loadInvitations = useCallback(() => {
    healthApi
      .invitations(cycle.id)
      .then((res) => {
        setInvitations(res.invitations);
        setLoadProblem(null);
      })
      .catch((err: unknown) => {
        // A failed load must never masquerade as an empty invite list.
        if (err instanceof HealthApiError && err.status === 401) {
          navigate(`/auth/login?returnTo=${encodeURIComponent(`/portal/health/${cycle.id}`)}`, {
            replace: true,
          });
          return;
        }
        setLoadProblem(err instanceof HealthApiError && err.status === 403 ? 'forbidden' : 'error');
      });
  }, [cycle.id, navigate]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const refresh = (): void => {
    loadInvitations();
    reload();
  };

  const run = (key: string, action: () => Promise<string>): void => {
    setBusy(key);
    setNotice(null);
    action()
      .then((message) => {
        setNotice(message);
        refresh();
      })
      .catch(() => setNotice('That did not go through. Please try again.'))
      .finally(() => setBusy(null));
  };

  const pendingIds = useMemo(
    () =>
      (invitations ?? [])
        .filter((inv) => inv.status === 'created' && inv.email)
        .map((inv) => inv.id),
    [invitations],
  );
  const pendingCount = pendingIds.length;

  // Batch sends are not all or nothing (Steve, 2026-07-03): pending rows can
  // be ticked and only the ticked ones are sent. No ticks means everyone.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const selectedPending = useMemo(
    () => pendingIds.filter((id) => selected.has(id)),
    [pendingIds, selected],
  );
  const toggleSelected = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isOpenMode = cycle.mode === 'open';
  const cycleIsOpen = cycle.status === 'open';
  // Church leaders follow participation read-only; only the assigned
  // facilitator (or an admin) manages the roster. The worker enforces this
  // with 403s regardless of what is rendered here.
  const canManage = detail.can_manage;

  return (
    <div>
      {notice && (
        <div
          className="health-banner health-banner-blue"
          role="status"
          style={{ marginBottom: 14 }}
        >
          {notice}
        </div>
      )}

      {/* Roster tools, or the shareable open link for open-mode cycles */}
      {!canManage ? (
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <p
            style={{
              fontSize: 13,
              color: 'var(--health-grey)',
              lineHeight: 1.6,
              margin: 0,
              flex: '1 1 260px',
            }}
          >
            You can follow participation here; the facilitator manages invitations.
          </p>
          {!isOpenMode && (
            <a className="btn btn-ghost btn-sm" href={exportUrls.invites(cycle.id)} download>
              Export invite list (CSV)
            </a>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            data-tip="Opens the survey in a new window, exactly as respondents see it — nothing is saved"
            onClick={() => window.open(`/portal/health/${cycle.id}/preview`, '_blank', 'noopener')}
          >
            Preview survey
          </button>
        </div>
      ) : isOpenMode ? (
        <OpenLinkPanel openLink={cycle.open_link} />
      ) : (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setShowAdd((s) => !s)}
            disabled={!cycleIsOpen}
          >
            {showAdd ? 'Close the add panel' : 'Add invitees'}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!cycleIsOpen || busy !== null || pendingCount === 0}
            onClick={() =>
              run('send-all', async () => {
                const ids = selectedPending.length > 0 ? selectedPending : undefined;
                const res = await healthApi.sendPendingInvites(cycle.id, ids);
                setSelected(new Set());
                return `Invitations sent: ${res.sent}.${res.failed > 0 ? ` ${res.failed} could not be sent; see the table below.` : ''}`;
              })
            }
          >
            {busy === 'send-all'
              ? 'Sending...'
              : selectedPending.length > 0
                ? `Send selected (${selectedPending.length})`
                : `Send all pending (${pendingCount})`}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={!cycleIsOpen || busy !== null}
            onClick={() =>
              run('remind-all', async () => {
                const res = await healthApi.remindNonResponders(cycle.id);
                return res.eligible === 0
                  ? 'No one is due a reminder right now (recent reminders and the reminder cap are respected).'
                  : `Reminders sent to ${res.sent} of ${res.eligible} people still to respond.`;
              })
            }
          >
            {busy === 'remind-all' ? 'Sending...' : 'Remind all non-responders'}
          </button>
          <a className="btn btn-ghost btn-sm" href={exportUrls.invites(cycle.id)} download>
            Export invite list (CSV)
          </a>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            data-tip="Opens the survey in a new window, exactly as respondents see it — nothing is saved"
            onClick={() => window.open(`/portal/health/${cycle.id}/preview`, '_blank', 'noopener')}
          >
            Preview survey
          </button>
        </div>
      )}

      {showAdd && !isOpenMode && canManage && (
        <AddInviteesPanel
          cycleId={cycle.id}
          onAdded={(message) => {
            setNotice(message);
            setShowAdd(false);
            refresh();
          }}
        />
      )}

      {/* The table */}
      <div className="card" style={{ padding: '8px 16px 4px' }}>
        {loadProblem === 'forbidden' ? (
          <p
            style={{
              padding: '16px 4px',
              color: 'var(--health-grey)',
              fontSize: 13.5,
              lineHeight: 1.7,
            }}
          >
            The invitation list for this assessment is not open to your account. If you believe you
            should have access, please contact the Registrar at registrar@confessinganglicans.nz.
          </p>
        ) : loadProblem === 'error' ? (
          <p
            style={{
              padding: '16px 4px',
              color: 'var(--health-grey)',
              fontSize: 13.5,
              lineHeight: 1.7,
            }}
          >
            The invitation list could not be loaded just now. Please try again shortly.
          </p>
        ) : invitations === null ? (
          <p style={{ padding: '12px 4px', color: 'var(--health-grey)', fontSize: 13 }}>
            Loading...
          </p>
        ) : invitations.length === 0 ? (
          <p
            style={{
              padding: '16px 4px',
              color: 'var(--health-grey)',
              fontSize: 13.5,
              lineHeight: 1.7,
            }}
          >
            {isOpenMode
              ? 'No one has opened the shared link yet. As people begin, their progress will appear here (anonymously).'
              : 'No invitations yet. Add the church’s leaders above and send their invitations when the list is ready.'}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {canManage && !isOpenMode && (
                    <th scope="col" style={{ width: 34 }}>
                      <input
                        type="checkbox"
                        aria-label="Select all pending invitations"
                        checked={pendingCount > 0 && selectedPending.length === pendingCount}
                        disabled={pendingCount === 0}
                        onChange={(e) =>
                          setSelected(e.target.checked ? new Set(pendingIds) : new Set())
                        }
                      />
                    </th>
                  )}
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Role</th>
                  <th scope="col">Status</th>
                  <th scope="col">Invited</th>
                  <th scope="col">Last emailed</th>
                  <th scope="col">Reminders</th>
                  {canManage && <th scope="col" aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {invitations.map((inv) => (
                  <InvitationRow
                    key={inv.id}
                    inv={inv}
                    cycleIsOpen={cycleIsOpen}
                    canManage={canManage}
                    busy={busy}
                    run={run}
                    showSelect={canManage && !isOpenMode}
                    selectable={inv.status === 'created' && Boolean(inv.email)}
                    isSelected={selected.has(inv.id)}
                    onToggleSelect={() => toggleSelected(inv.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── One row ──────────────────────────────────────────────────────────────────

function InvitationRow({
  inv,
  cycleIsOpen,
  canManage,
  busy,
  run,
  showSelect,
  selectable,
  isSelected,
  onToggleSelect,
}: {
  inv: InvitationSummary;
  cycleIsOpen: boolean;
  canManage: boolean;
  busy: string | null;
  run: (key: string, action: () => Promise<string>) => void;
  showSelect: boolean;
  selectable: boolean;
  isSelected: boolean;
  onToggleSelect: () => void;
}): JSX.Element {
  const who = inv.display_name || inv.email || 'Anonymous (open link)';
  const canEmail = Boolean(inv.email) && cycleIsOpen;
  const notSubmitted = inv.status !== 'submitted';
  const notDeclined = inv.status !== 'declined';

  return (
    <tr>
      {showSelect && (
        <td>
          {selectable && (
            <input
              type="checkbox"
              aria-label={`Select ${who} for sending`}
              checked={isSelected}
              onChange={onToggleSelect}
            />
          )}
        </td>
      )}
      <td style={{ fontWeight: 600 }}>{inv.display_name ?? (inv.email ? '' : 'Anonymous')}</td>
      <td>{inv.email ?? ''}</td>
      <td>
        {canManage ? (
          <>
            <label
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
              }}
              htmlFor={`role-${inv.id}`}
            >
              Role for {who}
            </label>
            <select
              id={`role-${inv.id}`}
              value={inv.role ?? ''}
              disabled={busy !== null || !cycleIsOpen}
              onChange={(e) => {
                const role = e.target.value;
                if (!role) return;
                run(`role-${inv.id}`, async () => {
                  await healthApi.patchInvitation(inv.id, { action: 'set_role', role });
                  return `Role updated for ${who}.`;
                });
              }}
              style={{
                border: '1px solid #cfc8b8',
                borderRadius: 6,
                padding: '3px 6px',
                fontFamily: 'inherit',
                fontSize: 12.5,
                background: '#fff',
                color: 'inherit',
              }}
            >
              <option value="">Not set</option>
              {ROLE_ORDER.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </>
        ) : (
          <span style={inv.role ? undefined : { color: 'var(--health-grey)' }}>
            {roleLabel(inv.role) || 'Not set'}
          </span>
        )}
      </td>
      <td>
        <span className={statusPillClass(inv.status)}>{invitationStatusLabel(inv.status)}</span>
        {inv.delivery_state === 'bounced' && inv.status !== 'bounced' && (
          <span className="pill pill-danger" style={{ marginLeft: 6 }}>
            Bounced
          </span>
        )}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>{formatNzDateShort(inv.invited_at)}</td>
      <td style={{ whiteSpace: 'nowrap' }}>{formatNzDateShort(inv.last_email_at)}</td>
      <td>{inv.reminder_count}</td>
      {canManage && (
        <td style={{ whiteSpace: 'nowrap' }}>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            {canEmail && notDeclined && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busy !== null}
                onClick={() =>
                  run(`resend-${inv.id}`, async () => {
                    const res = await healthApi.resendInvitation(inv.id);
                    return res.ok
                      ? `Invitation re-sent to ${who}.`
                      : `The invitation to ${who} could not be sent.`;
                  })
                }
              >
                Resend
              </button>
            )}
            {canEmail && notSubmitted && notDeclined && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busy !== null}
                onClick={() =>
                  run(`remind-${inv.id}`, async () => {
                    const res = await healthApi.remindInvitation(inv.id);
                    return res.ok
                      ? `Reminder sent to ${who}.`
                      : `The reminder to ${who} could not be sent.`;
                  })
                }
              >
                Remind
              </button>
            )}
            {notSubmitted && notDeclined && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busy !== null}
                onClick={() =>
                  run(`decline-${inv.id}`, async () => {
                    await healthApi.patchInvitation(inv.id, { action: 'decline' });
                    return `${who} marked as declined. No further emails will be sent to them.`;
                  })
                }
              >
                Mark declined
              </button>
            )}
            {!inv.submitted_at && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={busy !== null}
                onClick={() => {
                  if (!window.confirm(`Remove ${who} from the invite list?`)) return;
                  run(`remove-${inv.id}`, async () => {
                    await healthApi.patchInvitation(inv.id, { action: 'remove' });
                    return `${who} removed from the invite list.`;
                  });
                }}
              >
                Remove
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

// ── Open-mode link panel ─────────────────────────────────────────────────────

function OpenLinkPanel({ openLink }: { openLink: string | null }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ fontSize: 14.5, marginBottom: 6 }}>Shareable link</h3>
      <p style={{ fontSize: 13, color: 'var(--health-grey)', lineHeight: 1.6, marginBottom: 10 }}>
        This cycle uses one shared link that the church circulates itself. Anyone who opens it
        receives their own private way back to their answers. Because no email addresses are held,
        there is no roster and no reminders from here.
      </p>
      {openLink ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <code
            style={{
              background: 'var(--health-cream)',
              border: '1px solid #e5ddc9',
              borderRadius: 8,
              padding: '7px 10px',
              fontSize: 12.5,
              wordBreak: 'break-all',
            }}
          >
            {openLink}
          </code>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => {
              void navigator.clipboard.writeText(openLink).then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              });
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--health-grey)' }}>
          The shared link is not available yet.
        </p>
      )}
    </div>
  );
}

// ── Add invitees ─────────────────────────────────────────────────────────────

function AddInviteesPanel({
  cycleId,
  onAdded,
}: {
  cycleId: string;
  onAdded: (message: string) => void;
}): JSX.Element {
  const [pasted, setPasted] = useState('');
  const [suggestions, setSuggestions] = useState<InviteSuggestion[] | null>(null);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [pickedRoles, setPickedRoles] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    healthApi
      .suggestions(cycleId)
      .then((res) => setSuggestions(res.suggestions))
      .catch(() => setSuggestions([]));
  }, [cycleId]);

  const parsed = useMemo(() => parseInviteeLines(pasted), [pasted]);

  const submit = (): void => {
    const fromSuggestions = (suggestions ?? [])
      .filter((s) => s.email && picked[s.zoho_contact_id])
      .map((s) => {
        // The CRM-derived role is the default; a per-person override wins.
        const role = pickedRoles[s.zoho_contact_id] || s.suggested_role;
        return {
          name: s.name,
          email: s.email ?? '',
          ...(role ? { role } : {}),
        };
      });
    const invitees = [...parsed.invitees, ...fromSuggestions];
    if (invitees.length === 0) {
      setProblem('Nothing to add yet. Paste some lines or tick some names first.');
      return;
    }
    setSaving(true);
    setProblem(null);
    healthApi
      .addInvitees(cycleId, invitees)
      .then((res) => {
        onAdded(
          `Added ${res.added} ${res.added === 1 ? 'person' : 'people'} to the invite list.` +
            (res.skipped > 0
              ? ` ${res.skipped} skipped (already on the list or no usable email).`
              : '') +
            ' Invitations are not sent until you choose Send.',
        );
      })
      .catch(() => setProblem('The invitees could not be added. Please try again.'))
      .finally(() => setSaving(false));
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ fontSize: 14.5, marginBottom: 6 }}>Add invitees</h3>
      <p style={{ fontSize: 13, color: 'var(--health-grey)', lineHeight: 1.6, marginBottom: 10 }}>
        Paste one person per line as <strong>name, email, role</strong> (role is optional: clergy,
        warden, parish council, ministry leader, staff, or other). You can also tick people already
        known to the church below.
      </p>
      <textarea
        className="health-textarea"
        style={{ minHeight: 100, fontFamily: 'inherit' }}
        placeholder={'Jane Smith, jane@example.org, warden\nRewi Brown, rewi@example.org'}
        value={pasted}
        onChange={(e) => setPasted(e.target.value)}
        aria-label="Paste invitees, one per line: name, email, role"
      />
      {parsed.rejected.length > 0 && (
        <p style={{ fontSize: 12.5, color: 'var(--band-priority)', marginTop: 6 }}>
          {parsed.rejected.length} {parsed.rejected.length === 1 ? 'line has' : 'lines have'} no
          usable email and will be skipped: {parsed.rejected.join(' | ')}
        </p>
      )}

      {suggestions && suggestions.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <h4 style={{ fontSize: 13, marginBottom: 8 }}>Known to this church</h4>
          <div style={{ display: 'grid', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
            {suggestions.map((s) => (
              <div
                key={s.zoho_contact_id}
                style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
              >
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13.5,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={picked[s.zoho_contact_id] ?? false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setPicked((prev) => ({ ...prev, [s.zoho_contact_id]: checked }));
                      // Pre-fill the role from the CRM the first time a person
                      // is ticked; the select below stays free to change it.
                      if (checked) {
                        setPickedRoles((prev) =>
                          prev[s.zoho_contact_id]
                            ? prev
                            : { ...prev, [s.zoho_contact_id]: s.suggested_role },
                        );
                      }
                    }}
                  />
                  <span>
                    {s.name}{' '}
                    <span style={{ color: 'var(--health-grey)', fontSize: 12.5 }}>{s.email}</span>
                  </span>
                </label>
                {!picked[s.zoho_contact_id] && (
                  <span className="pill pill-neutral">{roleLabel(s.suggested_role)}</span>
                )}
                {picked[s.zoho_contact_id] && (
                  <select
                    aria-label={`Role for ${s.name}`}
                    value={pickedRoles[s.zoho_contact_id] ?? ''}
                    onChange={(e) =>
                      setPickedRoles((prev) => ({ ...prev, [s.zoho_contact_id]: e.target.value }))
                    }
                    style={{
                      border: '1px solid #cfc8b8',
                      borderRadius: 6,
                      padding: '2px 6px',
                      fontFamily: 'inherit',
                      fontSize: 12.5,
                      background: '#fff',
                    }}
                  >
                    <option value="">Role not set</option>
                    {ROLE_ORDER.map((r) => (
                      <option key={r} value={r}>
                        {roleLabel(r)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {problem && (
        <p style={{ fontSize: 13, color: 'var(--band-priority)', marginTop: 10 }}>{problem}</p>
      )}
      <div style={{ marginTop: 14 }}>
        <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={submit}>
          {saving ? 'Adding...' : 'Add to invite list'}
        </button>
      </div>
    </div>
  );
}
